import type { SlaState } from '@beam/core/sla';
import { statusLabel, type Status } from '@beam/core/status';
import { cn } from 'cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

/**
 * Days in the current status, computed by the server from the transition log
 * and sla.json. Red and amber come only from `slaState`; the tooltip shows
 * the allowance so the audience can see that red is computed, not applied.
 */
export function DaysCell({ days, sla, allowedDays, status, className }: { days: number; sla: SlaState; allowedDays: number | null; status: Status; className?: string }) {
  const sentence = allowedDays === null
    ? `${days} ${days === 1 ? 'day' : 'days'} at ${status} ${statusLabel(status)}. No allowance set for ${status} ${statusLabel(status)}.`
    : `${days} ${days === 1 ? 'day' : 'days'} at ${status} ${statusLabel(status)}. Allowed: ${allowedDays}.`;
  // Not a tab stop: thirty focusable cells would bury the Open links for a
  // keyboard user (audit finding). The sentence is read by assistive
  // technology and shown on hover.
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          data-slot="days-cell"
          data-sla={sla}
          className={cn(
            'tabular inline-block rounded-sm px-0.5',
            sla === 'overdue' && 'font-semibold text-beam-error',
            sla === 'warning' && 'font-semibold text-beam-warning',
            className,
          )}
        >
          <span aria-hidden="true">{days}</span>
          <span className="sr-only">{sentence}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{sentence}</TooltipContent>
    </Tooltip>
  );
}
